import React from "react";
import {
	Box,
	CircularProgress,
	Container,
	Typography
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMyTalentSelections } from "../../hooks/useAnonymizedTalents";
import { zoullooColors } from "../../theme";
import { TalentSelectionSummary } from "../../models/TalentSelection";

/**
 * "Mes sélections" — the CSM lands here from the side menu. Lists every
 * named selection they've saved, with summary stats (link counts + total views).
 *
 * Clicking a row opens its detail page where per-link actions live.
 */
const TalentSelectionsList: React.FC = () => {
	const { t } = useTranslation();
	const { data: selections = [], isLoading, isError } = useMyTalentSelections();
	const navigate = useNavigate();

	return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
			<Box sx={{ mb: 4 }}>
				<Typography
					variant="h2"
					sx={{ color: zoullooColors.secondary.dark, mb: 1 }}
				>
					{t("anonymizedTalent.csmListTitle")}
				</Typography>
				<Typography sx={{ color: zoullooColors.text.secondary }}>
					{t("anonymizedTalent.csmListSubtitle")}
				</Typography>
			</Box>

			{isLoading && (
				<Box display="flex" justifyContent="center" sx={{ py: 8 }}>
					<CircularProgress />
				</Box>
			)}

			{isError && !isLoading && (
				<Box
					sx={{
						p: 4,
						textAlign: "center",
						backgroundColor: zoullooColors.error.alpha.lighter,
						border: `1px solid ${zoullooColors.error.light}`,
						borderRadius: 2,
						color: zoullooColors.error.dark
					}}
				>
					{t("anonymizedTalent.csmListLoadError")}
				</Box>
			)}

			{!isLoading && !isError && selections.length === 0 && <EmptyState />}

			{!isLoading && !isError && selections.length > 0 && (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
					{selections.map((s) => (
						<SelectionRow
							key={s.id}
							summary={s}
							onClick={() => navigate(`/talent-selections/${s.id}`)}
						/>
					))}
				</Box>
			)}
		</Container>
	);
};

const SelectionRow: React.FC<{
	summary: TalentSelectionSummary;
	onClick: () => void;
}> = ({ summary, onClick }) => {
	const { t, i18n } = useTranslation();
	const createdAt = new Date(summary.createdAt).toLocaleDateString(i18n.language, {
		day: "2-digit",
		month: "long",
		year: "numeric"
	});

	return (
		<Box
			role="button"
			tabIndex={0}
			onClick={onClick}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					onClick();
				}
			}}
			sx={{
				p: 2.5,
				border: `1px solid ${zoullooColors.grey[200]}`,
				borderRadius: 2,
				backgroundColor: "white",
				cursor: "pointer",
				display: "grid",
				gridTemplateColumns: { xs: "1fr", md: "1fr auto auto auto" },
				alignItems: "center",
				gap: 2,
				transition: "border-color 120ms, box-shadow 120ms",
				"&:hover": {
					borderColor: zoullooColors.primary.main,
					boxShadow: zoullooColors.shadow.medium
				}
			}}
		>
			<Box>
				<Typography
					sx={{
						fontWeight: 700,
						fontSize: 16,
						color: zoullooColors.secondary.dark
					}}
				>
					{summary.title}
				</Typography>
				<Typography sx={{ fontSize: 12, color: zoullooColors.text.secondary }}>
					{t("anonymizedTalent.csmListCreatedAt", { date: createdAt })}
				</Typography>
			</Box>
			<Stat label={t("anonymizedTalent.csmListLinks")} value={summary.linksCount} />
			<Stat
				label={t("anonymizedTalent.csmListActiveLinks")}
				value={summary.activeLinksCount}
				highlight={summary.activeLinksCount > 0}
			/>
			<Stat label={t("anonymizedTalent.csmListViews")} value={summary.totalViewCount} />
		</Box>
	);
};

const Stat: React.FC<{
	label: string;
	value: number;
	highlight?: boolean;
}> = ({ label, value, highlight }) => (
	<Box sx={{ textAlign: "center", minWidth: 72 }}>
		<Typography
			sx={{
				fontSize: 18,
				fontWeight: 800,
				color: highlight
					? zoullooColors.primary.main
					: zoullooColors.secondary.dark
			}}
		>
			{value}
		</Typography>
		<Typography
			sx={{
				fontSize: 10,
				textTransform: "uppercase",
				letterSpacing: 1,
				color: zoullooColors.text.secondary,
				fontWeight: 600
			}}
		>
			{label}
		</Typography>
	</Box>
);

const EmptyState: React.FC = () => {
	const { t } = useTranslation();
	return (
		<Box
			sx={{
				p: 6,
				textAlign: "center",
				backgroundColor: zoullooColors.proposal.magnolia,
				borderRadius: 2,
				border: `1px dashed ${zoullooColors.secondary.light}`
			}}
		>
			<Typography
				sx={{
					fontSize: 18,
					fontWeight: 700,
					color: zoullooColors.secondary.dark,
					mb: 1
				}}
			>
				{t("anonymizedTalent.csmListEmptyTitle")}
			</Typography>
			<Typography sx={{ fontSize: 14, color: zoullooColors.text.secondary }}>
				{t("anonymizedTalent.csmListEmptySubtitle")}
			</Typography>
		</Box>
	);
};

export default TalentSelectionsList;
